import "./ForgotPassword.scss";

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "@elements/Header";
import { useTitle } from "@hooks/useTitle";
import { Button, FormGroup, Label, TextField } from "@runwayhealth/runway-components-react";
import { RESTORE_PASSWORD } from "@store/user/userActions";
import { RootEntity, RootState } from "@types-runway/state";
import { User } from "@types-runway/user";

// The browser strips surrounding spaces from email inputs, so spaces-only reads as empty.
const describeEmailProblem = (input: HTMLInputElement): string | undefined => {
  if (input.value === "") {
    return "Please enter your email address.";
  }
  if (input.validity.typeMismatch) {
    return "Enter an email address like name@example.com.";
  }
  return undefined;
};

const ForgotPasswordPage = () => {
  const dispatch = useDispatch();

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errorText, setErrorText] = useState("");
  const isInvalid = Boolean(errorText);
  const user = useSelector<RootState, RootEntity<User>>((state) => state.user);
  useTitle("Password Reset");

  const [email, setEmail] = useState("");
  const emailRef = useRef<HTMLInputElement>(null);

  const submitEmail = () => {
    const emailProblem = emailRef.current && describeEmailProblem(emailRef.current);
    if (emailProblem) {
      setErrorText(emailProblem);
      emailRef.current?.focus();
      return;
    }
    setHasSubmitted(true);
    dispatch({
      type: RESTORE_PASSWORD,
      email,
    });
  };
  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    setErrorText("");
    setEmail(e.currentTarget.value);
  };

  // Only errors from this page's own submit; user.error is persisted across reloads.
  // hasSubmitted is left out of the deps: it turns true before any such error arrives.
  useEffect(() => {
    if (hasSubmitted && user.error.message) {
      setErrorText(user.error.message);
    }
  }, [user.error]);

  return (
    <>
      <Header hasProgressBar={false} />
      <div className="forgot-password-page">
        <div className="password-container">
          <div className="forgot-password-container">
            <div className="forgot-password-content">
              <div className="forgot-password-form">
                <label className="forgot-password"> Forgot your password? </label>
                <p>Enter your email address and we'll send a link to reset your password.</p>
                <div className="form">
                  <FormGroup className="forgot-password-field">
                    <Label htmlFor="forgot-password-email">Email</Label>
                    <TextField
                      ref={emailRef}
                      id="forgot-password-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      isInvalid={isInvalid}
                      aria-invalid={isInvalid}
                      aria-describedby={isInvalid ? "forgot-password-error" : undefined}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  <div className="button-div">
                    <Button
                      size="lg"
                      onClick={submitEmail}
                      iconName="ArrowRight"
                      isLoading={user.isPending}
                      disabled={user.isPending}
                      isBlock
                    >
                      Reset password
                    </Button>
                  </div>
                </div>
                {errorText && (
                  <div id="forgot-password-error" className="error-message-forgot" role="alert">
                    <span className="message-content-forgot">{errorText}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPasswordPage;
